import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  PlayIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { manufacturingService, WorkOrder } from '../../services/manufacturing.service';
import AddManufacturingOrderModal from '../../components/manufacturing/AddManufacturingOrderModal';
import ViewWorkOrderModal from '../../components/manufacturing/ViewWorkOrderModal';
import EditWorkOrderModal from '../../components/manufacturing/EditWorkOrderModal';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { BatchPicker, BatchSelection, batchList } from '../../components/common/BatchPicker';
import {
  ConfirmDialog,
  Field,
  FormActions,
  getApiErrorMessage,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import toast from 'react-hot-toast';
import { defaultRowsPerPage } from '../../lib/preferences';
import { PlantTag } from '../../components/common/PlantField';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATUS_META: Record<WorkOrder['status'], { label: string; color: string; bar: string }> = {
  draft: { label: 'Draft', color: 'text-gray-700 bg-gray-50 ring-gray-500/20', bar: 'bg-gray-400' },
  planned: { label: 'Planned', color: 'text-blue-700 bg-blue-50 ring-blue-600/20', bar: 'bg-blue-500' },
  in_progress: { label: 'In Progress', color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20', bar: 'bg-yellow-500' },
  completed: { label: 'Completed', color: 'text-green-700 bg-green-50 ring-green-600/20', bar: 'bg-green-500' },
  cancelled: { label: 'Cancelled', color: 'text-red-700 bg-red-50 ring-red-600/20', bar: 'bg-red-400' },
  on_hold: { label: 'On Hold', color: 'text-orange-700 bg-orange-50 ring-orange-600/20', bar: 'bg-orange-500' },
};

const PRIORITY_META: Record<WorkOrder['priority'], { label: string; color: string }> = {
  urgent: { label: 'Urgent', color: 'text-red-700 bg-red-50 ring-red-600/20' },
  high: { label: 'High', color: 'text-orange-700 bg-orange-50 ring-orange-600/20' },
  medium: { label: 'Medium', color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20' },
  low: { label: 'Low', color: 'text-green-700 bg-green-50 ring-green-600/20' },
};

const FALLBACK_BADGE = 'text-gray-700 bg-gray-50 ring-gray-500/20';

const formatNumber = (value: number) =>
  Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const isOverdue = (workOrder: WorkOrder) => {
  if (workOrder.status === 'completed' || workOrder.status === 'cancelled') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(workOrder.plannedEndDate) < today;
};

// The materials of the work order that have batches in stock, each with its batches to take from
const CompletionBatches: React.FC<{
  workOrderId: number;
  value: Record<string, BatchSelection>;
  onChange: (value: Record<string, BatchSelection>) => void;
}> = ({ workOrderId, value, onChange }) => {
  const { data: workOrder } = useQuery({
    queryKey: ['workOrders', 'one', workOrderId],
    queryFn: () => manufacturingService.getWorkOrder(workOrderId),
  });
  const materials = ((workOrder as { materialConsumptions?: unknown[] } | undefined)?.materialConsumptions ?? []) as { materialId: string; plannedQuantity: number; actualQuantity?: number; material?: { name?: string; unit?: string } }[];
  if (materials.length === 0) return null;
  return (
    <div className="space-y-2">
      {materials.map((material) => {
        const consumed = Number(material.actualQuantity) > 0 ? Number(material.actualQuantity) : Number(material.plannedQuantity);
        return (
          <BatchPicker
            key={material.materialId}
            productId={material.materialId}
            idPrefix={`cb-${material.materialId}`}
            value={value[material.materialId] ?? {}}
            onChange={(selection) => onChange({ ...value, [material.materialId]: selection })}
            label={`${material.material?.name ?? 'Material'}: ${formatNumber(consumed)} ${material.material?.unit ?? ''} consumed, from batches`}
          />
        );
      })}
    </div>
  );
};

const ManufacturingList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WorkOrder['status'] | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<WorkOrder['priority'] | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null);
  const [startTarget, setStartTarget] = useState<WorkOrder | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WorkOrder | null>(null);
  const [completeTarget, setCompleteTarget] = useState<WorkOrder | null>(null);
  const [completedQuantity, setCompletedQuantity] = useState('');
  const [rejectedQuantity, setRejectedQuantity] = useState('0');
  // material id -> batch id -> quantity used from it
  const [materialBatches, setMaterialBatches] = useState<Record<string, BatchSelection>>({});
  const [producedBatch, setProducedBatch] = useState('');
  const [producedHeat, setProducedHeat] = useState('');
  const queryClient = useQueryClient();

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const queryParams = {
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
  };

  const { data: workOrdersPage, isLoading, isFetching, error } = useQuery({
    queryKey: ['workOrders', 'page', queryParams],
    queryFn: () => manufacturingService.getWorkOrdersPage(queryParams),
    placeholderData: keepPreviousData,
  });

  const { data: summary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['workOrders', 'summary'],
    queryFn: () => manufacturingService.getWorkOrderSummary(),
  });

  const workOrders = workOrdersPage?.data ?? [];
  const total = workOrdersPage?.total ?? 0;
  const totalPages = workOrdersPage?.totalPages ?? 1;

  // Step back when the current page no longer exists
  useEffect(() => {
    if (workOrdersPage && page > 1 && page > workOrdersPage.totalPages) {
      setPage(Math.max(1, workOrdersPage.totalPages));
    }
  }, [workOrdersPage, page]);

  const startWorkOrderMutation = useMutation({
    mutationFn: (id: number) => manufacturingService.startWorkOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrders'] });
      toast.success('Work order started');
      setStartTarget(null);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Could not start the work order'));
    },
  });

  const completeWorkOrderMutation = useMutation({
    mutationFn: ({ id, completed, rejected }: { id: number; completed: number; rejected: number }) =>
      manufacturingService.completeWorkOrder(id, completed, rejected, {
        materialBatches: Object.entries(materialBatches).flatMap(([materialId, selection]) => batchList(selection).map((entry) => ({ materialId, ...entry }))),
        batchNumber: producedBatch.trim() || undefined,
        heatNumber: producedHeat.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrders'] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      // Completing adds finished goods and consumes materials
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Work order completed');
      setCompleteTarget(null);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Could not complete the work order'));
    },
  });

  const deleteWorkOrderMutation = useMutation({
    mutationFn: (id: number) => manufacturingService.deleteWorkOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workOrders'] });
      toast.success('Work order deleted');
      setDeleteTarget(null);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Could not delete the work order'));
    },
  });

  const openCompleteDialog = (workOrder: WorkOrder) => {
    setCompletedQuantity(String(Number(workOrder.plannedQuantity)));
    setRejectedQuantity('0');
    setMaterialBatches({});
    setProducedBatch('');
    setProducedHeat('');
    setCompleteTarget(workOrder);
  };

  const completedValue = parseFloat(completedQuantity);
  const rejectedValue = parseFloat(rejectedQuantity || '0');
  const isCompleteValid =
    Number.isFinite(completedValue) && completedValue > 0 && Number.isFinite(rejectedValue) && rejectedValue >= 0;

  const handleCompleteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!completeTarget || !isCompleteValid) return;
    completeWorkOrderMutation.mutate({
      id: completeTarget.id,
      completed: completedValue,
      rejected: rejectedValue,
    });
  };

  const handleViewWorkOrder = (workOrder: WorkOrder) => {
    setSelectedWorkOrder(workOrder);
    setIsViewModalOpen(true);
  };

  const handleEditWorkOrder = (workOrder: WorkOrder) => {
    setSelectedWorkOrder(workOrder);
    setIsEditModalOpen(true);
  };

  const hasFilters = !!(search || statusFilter || priorityFilter);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPriorityFilter('');
    setPage(1);
  };

  const stats = [
    { label: 'Total Orders', value: summary?.total ?? 0, icon: ClipboardDocumentListIcon, tone: 'text-indigo-600 bg-indigo-50' },
    { label: 'In Progress', value: summary?.inProgress ?? 0, icon: Cog6ToothIcon, tone: 'text-yellow-600 bg-yellow-50' },
    { label: 'Completed', value: summary?.completed ?? 0, icon: CheckCircleIcon, tone: 'text-green-600 bg-green-50' },
    { label: 'Overdue', value: summary?.overdue ?? 0, icon: ExclamationTriangleIcon, tone: 'text-red-600 bg-red-50' },
  ];

  const selectClass =
    'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
  const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
  const badgeClass = 'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset';

  if (error) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-red-500">Error loading manufacturing orders</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Manufacturing Orders</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage production orders and work orders
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Manufacturing Order
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">
                {isSummaryLoading ? '—' : formatNumber(stat.value)}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search & filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by work order number, product name, or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as WorkOrder['status'] | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All statuses</option>
              {(Object.keys(STATUS_META) as WorkOrder['status'][]).map((status) => (
                <option key={status} value={status}>
                  {STATUS_META[status].label}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by priority"
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value as WorkOrder['priority'] | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All priorities</option>
              {(Object.keys(PRIORITY_META) as WorkOrder['priority'][]).map((priority) => (
                <option key={priority} value={priority}>
                  {PRIORITY_META[priority].label}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Work Orders Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Work Order #</th>
                <th className={thClass}>Product</th>
                <th className={thClass}>Quantity</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Priority</th>
                <th className={thClass}>Due Date</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-sm text-gray-500 text-center" colSpan={7}>
                    Loading manufacturing orders...
                  </td>
                </tr>
              ) : workOrders.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={7}>
                    <ClipboardDocumentListIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No manufacturing orders found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? 'Try changing or clearing the search and filters.'
                        : 'Add your first manufacturing order to start tracking production.'}
                    </p>
                  </td>
                </tr>
              ) : (
                workOrders.map((workOrder) => {
                  const status = STATUS_META[workOrder.status];
                  const priority = PRIORITY_META[workOrder.priority];
                  const plannedQuantity = Number(workOrder.plannedQuantity) || 0;
                  const progress =
                    plannedQuantity > 0
                      ? Math.min(100, Math.max(0, (Number(workOrder.completedQuantity) / plannedQuantity) * 100))
                      : 0;
                  const overdue = isOverdue(workOrder);
                  return (
                    <tr key={workOrder.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                          {workOrder.workOrderNumber}
                        </span>
                        <PlantTag plantId={workOrder.plantId} className="mt-1" />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{workOrder.product?.name || 'N/A'}</div>
                        <div className="text-gray-500">{workOrder.product?.sku || ''}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <div className="w-40">
                          <div className="text-gray-900">
                            <span className="font-medium">{formatNumber(workOrder.completedQuantity)}</span>
                            <span className="text-gray-500">
                              {' '}/ {formatNumber(workOrder.plannedQuantity)}
                              {workOrder.unit && ` ${workOrder.unit}`}
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100">
                            <div
                              className={`h-1.5 rounded-full ${status?.bar || 'bg-gray-400'}`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className={`${badgeClass} ${status?.color || FALLBACK_BADGE}`}>
                          {status?.label || workOrder.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className={`${badgeClass} ${priority?.color || FALLBACK_BADGE}`}>
                          {priority?.label || workOrder.priority}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <div className={overdue ? 'font-medium text-red-600' : 'text-gray-700'}>
                          {formatDate(workOrder.plannedEndDate)}
                        </div>
                        {overdue && <div className="text-xs text-red-600">Overdue</div>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1">
                          {workOrder.status === 'planned' && (
                            <button
                              onClick={() => setStartTarget(workOrder)}
                              disabled={startWorkOrderMutation.isPending}
                              className="inline-flex items-center rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                            >
                              <PlayIcon className="h-3.5 w-3.5 mr-1" />
                              Start
                            </button>
                          )}
                          {workOrder.status === 'in_progress' && (
                            <button
                              onClick={() => openCompleteDialog(workOrder)}
                              disabled={completeWorkOrderMutation.isPending}
                              className="inline-flex items-center rounded-md bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50"
                            >
                              <CheckIcon className="h-3.5 w-3.5 mr-1" />
                              Complete
                            </button>
                          )}
                          <button
                            onClick={() => handleEditWorkOrder(workOrder)}
                            className="text-blue-600 hover:text-blue-900 p-1.5 rounded hover:bg-blue-50"
                            title="Edit Work Order"
                            aria-label="Edit Work Order"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleViewWorkOrder(workOrder)}
                            className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded hover:bg-indigo-50"
                            title="View Details"
                            aria-label="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {(workOrder.status === 'draft' || workOrder.status === 'cancelled') && (
                            <button
                              onClick={() => setDeleteTarget(workOrder)}
                              className="text-red-600 hover:text-red-900 p-1.5 rounded hover:bg-red-50"
                              title="Delete Work Order"
                              aria-label="Delete Work Order"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} orders
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination
              className="lg:flex-1"
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      <AddManufacturingOrderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <ViewWorkOrderModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        workOrder={selectedWorkOrder}
        onEdit={(workOrder) => {
          setIsViewModalOpen(false);
          handleEditWorkOrder(workOrder);
        }}
      />

      <EditWorkOrderModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        workOrder={selectedWorkOrder}
      />

      {/* Start confirmation */}
      <ConfirmDialog
        isOpen={!!startTarget}
        onClose={() => setStartTarget(null)}
        onConfirm={() => startTarget && startWorkOrderMutation.mutate(startTarget.id)}
        title="Start Work Order"
        message={
          <>
            <span className="font-medium text-gray-900">{startTarget?.workOrderNumber}</span> will move
            to In Progress. Material availability is checked first.
          </>
        }
        confirmLabel="Start"
        pendingLabel="Starting..."
        isPending={startWorkOrderMutation.isPending}
        tone="primary"
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteWorkOrderMutation.mutate(deleteTarget.id)}
        title="Delete Work Order"
        message={
          <>
            <span className="font-medium text-gray-900">{deleteTarget?.workOrderNumber}</span> will be
            permanently deleted. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        isPending={deleteWorkOrderMutation.isPending}
      />

      {/* Complete work order */}
      <Modal
        isOpen={!!completeTarget}
        onClose={() => !completeWorkOrderMutation.isPending && setCompleteTarget(null)}
        title="Complete Work Order"
        description={completeTarget?.workOrderNumber}
        size="lg"
      >
        <form onSubmit={handleCompleteSubmit} className="space-y-5">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">{completeTarget?.product?.name || 'N/A'}</p>
            <p className="mt-0.5 text-xs text-gray-500">
              Planned quantity: {formatNumber(Number(completeTarget?.plannedQuantity))}
              {completeTarget?.unit ? ` ${completeTarget.unit}` : ''}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Completed Quantity" htmlFor="complete-quantity" required>
              <input
                type="number"
                id="complete-quantity"
                min="0.001"
                step="0.001"
                required
                value={completedQuantity}
                onChange={(e) => setCompletedQuantity(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Rejected Quantity" htmlFor="rejected-quantity">
              <input
                type="number"
                id="rejected-quantity"
                min="0"
                step="0.001"
                value={rejectedQuantity}
                onChange={(e) => setRejectedQuantity(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>

          {completeTarget && (
            <CompletionBatches
              workOrderId={completeTarget.id}
              value={materialBatches}
              onChange={setMaterialBatches}
            />
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Batch number of what is made" htmlFor="complete-batch" hint={`Left empty: ${completeTarget?.batchNumber || completeTarget?.workOrderNumber || 'the work order number'}`}>
              <input type="text" id="complete-batch" maxLength={50} value={producedBatch} onChange={(e) => setProducedBatch(e.target.value)} className={inputClass} />
            </Field>
            <Field label="Heat number" htmlFor="complete-heat" hint="Left empty: the heat of the material, when it is all of one heat">
              <input type="text" id="complete-heat" maxLength={50} value={producedHeat} onChange={(e) => setProducedHeat(e.target.value)} className={inputClass} />
            </Field>
          </div>

          <p className="text-sm text-gray-600">
            The completed quantity is added to the product's stock and the planned materials are
            deducted from inventory. This cannot be undone.
          </p>

          <FormActions>
            <button
              type="button"
              onClick={() => setCompleteTarget(null)}
              disabled={completeWorkOrderMutation.isPending}
              className={secondaryButtonClass}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={completeWorkOrderMutation.isPending || !isCompleteValid}
              className={primaryButtonClass}
            >
              {completeWorkOrderMutation.isPending ? 'Completing...' : 'Complete Work Order'}
            </button>
          </FormActions>
        </form>
      </Modal>
    </div>
  );
};

export default ManufacturingList;
